import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Container, Paper, TextField, Typography, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CheckCircleOutline } from "@mui/icons-material";
import clientBillingApi from "../../../api/clientBillingApi";
import { useAppSelector } from "../../../hooks";
import { CfdiUseForInvoice } from "../../../components/billing/CfdiUseForInvoice";
import { getTicketWarning, invoiceRequestSchema, type InvoiceRequestFormData } from "../../../components/billing/invoiceRequest.schema";

export const RequestInvoicePage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { client } = useAppSelector((state) => state.clientAuth);
  // Per-invoice CFDI use. Starts on the account default, so a client who
  // ignores the field gets exactly what they got before.
  const [cfdiUse, setCfdiUse] = useState(client?.cfdiUse || "");

  useEffect(() => {
    setCfdiUse(client?.cfdiUse || "");
  }, [client?.cfdiUse]);

  const { control, handleSubmit, formState: { errors } } = useForm<InvoiceRequestFormData>({
    resolver: zodResolver(invoiceRequestSchema),
    defaultValues: { invoiceNumber: "", amount: "" },
  });

  const onSubmit = async (data: InvoiceRequestFormData) => {
    try {
      setError(null);
      setSubmitting(true);

      const { data: result } = await clientBillingApi.post("/client-billing/invoices", {
        invoiceNumber: data.invoiceNumber.trim(),
        amount: Number(data.amount),
        cfdiUse,
      });

      if (result.ok) {
        setSuccess(true);
        setCfdiUse(client?.cfdiUse || "");
      }
    } catch (err: any) {
      const message = err.response?.data?.error?.message || "Error al solicitar la factura";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Solicitar factura
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {success ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CheckCircleOutline sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Solicitud recibida
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Tu factura se procesará automáticamente. Recibirás un correo electrónico con el PDF y XML cuando esté lista.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              También puedes ver el estado de tu factura en "Mis Facturas".
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button variant="outlined" onClick={() => navigate("/facturacion")}>
                Ver mis facturas
              </Button>
              <Button variant="contained" onClick={() => { setSuccess(false); setError(null); }}>
                Solicitar otra
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Ingresa el número de factura (ej: H04550) o el número de albarán (solo
              dígitos, ej: 16229) y el monto total que aparecen en tu ticket impreso.
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="invoiceNumber"
                control={control}
                render={({ field }) => {
                  const warning = getTicketWarning(field.value);
                  return (
                    <TextField
                      {...field}
                      label="Factura o albarán (ej: U05434 o 16229)"
                      fullWidth
                      margin="normal"
                      error={!!errors.invoiceNumber}
                      helperText={
                        errors.invoiceNumber?.message
                        || (warning ? warning : "Número de factura (letra + dígitos) o de albarán (solo dígitos)")
                      }
                      FormHelperTextProps={warning ? { sx: { color: "warning.main" } } : undefined}
                      autoFocus
                    />
                  );
                }}
              />

              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Monto total"
                    fullWidth
                    margin="normal"
                    error={!!errors.amount}
                    helperText={errors.amount?.message || "El monto total de tu ticket"}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: "text.secondary" }}>$</Typography> }}
                  />
                )}
              />

              <CfdiUseForInvoice
                value={cfdiUse}
                onChange={setCfdiUse}
                accountDefault={client?.cfdiUse}
                fiscalRegime={client?.fiscalRegime}
                rfc={client?.rfc}
                settingsHint="cámbialo en tu perfil"
              />

              <Button type="submit" variant="contained" fullWidth size="large" disabled={submitting} sx={{ mt: 3 }}>
                {submitting ? "Enviando..." : "Solicitar factura"}
              </Button>
            </form>
          </>
        )}
      </Paper>
    </Container>
  );
};
